import { useMemo, useCallback, useRef, useEffect } from 'react';
import { WeavableContextProps, SummaryFormat } from '../types';
import { useConfig, useContextId, useSummaries } from '../hooks';
import { themeToStyles } from '../utils';
import { SummaryCard } from './SummaryCard';

/**
 * Calculate ISO date X days back from now
 */
function getDateDaysBack(daysBack: number): string {
    const date = new Date();
    date.setDate(date.getDate() - daysBack);
    return date.toISOString();
}

/**
 * Get user's timezone
 */
function getUserTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/**
 * WeavableContext - Main component that fetches and displays summaries
 */
export function WeavableContext({ className, noSummariesMessage, onSummariesLoaded, theme }: WeavableContextProps) {
    // Convert theme overrides to CSS variables
    const themeStyles = useMemo(() => themeToStyles(theme), [theme]);
    // Store callback in ref to keep it up-to-date without causing re-renders
    const onSummariesLoadedRef = useRef(onSummariesLoaded);

    useEffect(() => {
        onSummariesLoadedRef.current = onSummariesLoaded;
    }, [onSummariesLoaded]);

    // Create stable callback wrapper that never changes identity
    const stableOnSummariesLoaded = useCallback((params: { fromDate: Date; toDate: Date }) => {
        onSummariesLoadedRef.current?.(params);
    }, []);

    // Step 1: Fetch config
    const { config, loading: configLoading, error: configError } = useConfig();

    // Calculate date range based on config
    const fromDateIso = useMemo(() => {
        if (!config) return '';
        return getDateDaysBack(config.defaultDaysBack);
    }, [config]);

    const timeZone = useMemo(() => getUserTimeZone(), []);

    // Step 2: Fetch context ID
    const { contextId, loading: contextLoading, error: contextError } = useContextId(fromDateIso, timeZone, !!config);

    // Step 3: Fetch summaries
    const {
        summaries,
        loading: summariesLoading,
        error: summariesError,
    } = useSummaries(
        contextId,
        config?.presets || [],
        SummaryFormat.Html,
        !!contextId && !!config,
        fromDateIso,
        stableOnSummariesLoaded,
    );

    // Handle errors
    const error = configError || contextError || summariesError;

    // Show loading state while fetching config, context or summaries
    if (configLoading || contextLoading || summariesLoading) {
        return (
            <div className={`weavable-container ${className || ''}`.trim()} style={themeStyles}>
                <div className="weavable-section">
                    <div className="weavable-card weavable-skeleton">
                        <div className="weavable-card-content">
                            <div className="weavable-skeleton-line"></div>
                            <div className="weavable-skeleton-line"></div>
                            <div className="weavable-skeleton-line" style={{ width: '60%' }}></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // show no ui on error–the error will be propagated to the callback
    if (error) {
        return null;
    }

    // Show summaries - each summary is a card within a section
    return (
        <div className={`weavable-container ${className || ''}`.trim()} style={themeStyles}>
            <div className="weavable-section">
                {summaries.map((summary) => (
                    <SummaryCard key={summary.preset.id} summary={summary} />
                ))}
                {summaries.length === 0 && (
                    <div className="weavable-empty">
                        <p>{noSummariesMessage || 'No summaries yet.'}</p>
                    </div>
                )}
            </div>
        </div>
    );
}
